import type { LiveRole } from "@/lib/api/live-api";
import { ROLL_VISIBLE_SECONDS } from "@/lib/studio/view/roll-horizon";

/**
 * Ce qu'un spectateur de récital attend en plus de son délai propre, en
 * millisecondes : de quoi voir les notes de l'interprète tomber du haut du
 * rouleau au lieu d'apparaître sur la ligne de lecture.
 *
 * # Le rouleau entier
 *
 * Et pas la part visible au-dessus de la tête de lecture, qui dépend de la
 * hauteur de l'écran. Changer de délai fait sauter la tête (voir
 * `LocalTransport.setPlayoutDelay`) : un spectateur qui tourne son téléphone
 * verrait le récital sauter d'une seconde. Le rouleau entier couvre tous les
 * écrans, au prix d'une note qui passe une fraction de seconde au-dessus du
 * haut avant d'entrer.
 */
export const LISTENER_HORIZON_MS = ROLL_VISIBLE_SECONDS * 1000;

/**
 * Le délai à poser sur le transport, en millisecondes : le délai propre — celui
 * que le service donne —, plus l'horizon chez un spectateur de récital.
 *
 * # Pourquoi ici et pas dans le service
 *
 * Partout ailleurs, chacun applique son délai propre tel quel, et c'est le
 * service qui le calcule. La règle protège ceux qui jouent ensemble : si l'un
 * attend et l'autre non, ils ne s'entendent plus au même instant, et le tempo
 * de l'ensemble s'effondre. Un spectateur ne joue pour personne, et son retard
 * ne change rien à ce que les autres entendent : la règle ne le concerne pas.
 * Le service n'a donc rien à en savoir, et le délai qu'il rend reste le trajet
 * réseau, ce que le panneau des autres affiche.
 *
 * Une première version passait l'horizon par l'annonce et le service. Elle
 * donnait exactement le même transport pour une signature de plus côté Go, un
 * champ de plus sur le fil, et un service qui devait connaître le rouleau.
 */
export function transportDelayMs(ownMs: number, role: LiveRole | null): number {
  if (role !== "listener") {
    return ownMs;
  }

  return ownMs + LISTENER_HORIZON_MS;
}
